import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "../components/ui/Button";
import { PageHeader } from "../components/Surface";
import { StatusLabel } from "../components/ListTable";
import { Icon } from "../app/Icon";
import { labelize } from "../lib/format";
import { getTicket, replyTicket, closeTicket, type Ticket, type TicketMessage } from "../lib/support";

export function SupportTicket() {
  const { id } = useParams();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [me, setMe] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  async function load() {
    if (!id) return;
    try {
      const r = await getTicket(id);
      setTicket(r.ticket);
      setMessages(r.messages);
      setMe(r.me);
    } catch {
      setNote("Could not load this ticket.");
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useEffect(() => endRef.current?.scrollIntoView({ block: "end" }), [messages.length]);

  const closed = ticket?.status === "closed";

  async function send(e: FormEvent) {
    e.preventDefault();
    if (!id || !body.trim()) return;
    setBusy(true);
    try {
      await replyTicket(id, body);
      setBody("");
      await load();
    } finally {
      setBusy(false);
    }
  }
  async function onClose() {
    if (!id) return;
    await closeTicket(id);
    await load();
  }

  return (
    <div className="mx-auto grid w-full max-w-2xl gap-4">
      <Link to="/support" className="flex w-max items-center gap-1 text-sm font-bold text-muted hover:text-ink">
        <Icon name="chevron" className="h-4 w-4 rotate-90" /> Support
      </Link>

      {ticket && (
        <PageHeader
          title={ticket.subject}
          subtitle={`${labelize(ticket.category)} · opened ${new Date(ticket.created_at).toLocaleDateString()}`}
          actions={
            <div className="flex items-center gap-2">
              <StatusLabel status={ticket.status} />
              {!closed && <Button variant="ghost" onClick={onClose}>Close</Button>}
            </div>
          }
        />
      )}
      {note && <p className="font-mono text-sm text-danger">{note}</p>}

      <div className="grid gap-2 rounded-card border border-line bg-surface p-4">
        {messages.map((m) => {
          const mine = m.author_account_id === me && !m.is_staff;
          return (
            <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
              {m.is_staff && <span className="px-1 font-mono text-[10px] font-bold uppercase tracking-widest text-accent-strong">RecruitTrucker support</span>}
              <span className={`inline-block max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm ${mine ? "bg-accent text-accent-ink" : "border border-line bg-surface-2"}`}>{m.body}</span>
              <span className="mt-0.5 px-1 font-mono text-[10px] text-muted">{new Date(m.created_at).toLocaleString()}</span>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {closed ? (
        <p className="font-mono text-sm text-muted">This ticket is closed. <Link to="/support" className="font-bold text-accent-strong">Open a new one →</Link></p>
      ) : (
        <form className="flex items-end gap-2" onSubmit={send}>
          <textarea
            className="max-h-40 min-h-[2.5rem] flex-1 resize-none rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink"
            rows={2}
            maxLength={5000}
            placeholder="Reply…"
            aria-label="Reply"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Button type="submit" disabled={busy || !body.trim()}>Send</Button>
        </form>
      )}
    </div>
  );
}
